import React, { ReactNode, useEffect, useRef, useState } from "react";
import {
  and,
  ControlProps,
  EnumCellProps,
  EnumOption,
  formatIs,
  RankedTester,
  rankWith,
  WithClassname,
} from "@jsonforms/core";
import {
  withJsonFormsOneOfEnumCellProps,
  withTranslateProps,
} from "@jsonforms/react";
import { WithOptionLabel } from "@jsonforms/material-renderers";
import { ILabDetailsData, ILabTest } from "@home-health-notify/domain";
import { Box, styled, Typography } from "@mui/material";
import theme from "../../../theme";
import {
  JsonFormsAutocomplete,
  JsonFormsAutocompleteHandle,
} from "./common/JsonFormsAutocomplete";

export const LabTestAutocompleteCell = (
  props: EnumCellProps &
    Partial<ControlProps> &
    Partial<WithClassname> &
    Partial<WithOptionLabel>,
) => {
  const autoCompleteRef = useRef<JsonFormsAutocompleteHandle>(null);

  const clear = (initialOptions: EnumOption[]) => {
    autoCompleteRef.current?.clear(initialOptions);
  };

  const labTestToFilterOption = (row: ILabTest): EnumOption => {
    return {
      label: row.name,
      value: JSON.stringify({
        loincCode: row.loincCode,
        specimenType: row.specimenType,
        panelType: row.panelType,
        specimenSource: row.specimenSource,
        type: row.type,
      }),
    };
  };

  const getInitialFilterOptions = (): {
    initialOptions: EnumOption[];
    initialOption?: EnumOption;
  } => {
    const selectedLab: ILabDetailsData = props.config?.selectedLab;
    if (!selectedLab) {
      return { initialOptions: [], initialOption: undefined };
    }

    const initialOptions = selectedLab.compendium.map(row =>
      labTestToFilterOption(row),
    );

    if (!props.data) {
      return { initialOptions, initialOption: undefined };
    }

    const selectedOption = JSON.parse(props.data);

    const initialOption = initialOptions.find(opt => {
      const optValue = JSON.parse(opt.value);
      return optValue.loincCode === selectedOption.loincCode;
    });

    return { initialOptions, initialOption };
  };

  const renderOption = (
    optionProps: React.HTMLAttributes<HTMLLIElement>,
    option: EnumOption,
  ): ReactNode => {
    const { specimenType, panelType, specimenSource, type } = JSON.parse(
      option.value,
    );
    const name = option.label;
    return (
      <Box key={option.value} component="li" {...optionProps}>
        <TestNameContainer>
          <Box>
            <TestName variant="body2">{name}</TestName>
            <TestType variant="body2">({type})</TestType>
          </Box>
          {!!specimenType && (
            <InfoItemContainer>
              <InfoItemLabel variant="body2" color="text.secondary">
                Specimen Type:
              </InfoItemLabel>
              <InfoItemValue variant="body2">{specimenType}</InfoItemValue>
            </InfoItemContainer>
          )}
          {!!specimenSource && (
            <InfoItemContainer>
              <InfoItemLabel variant="body2" color="text.secondary">
                Specimen Source:
              </InfoItemLabel>
              <InfoItemValue variant="body2">{specimenSource}</InfoItemValue>
            </InfoItemContainer>
          )}
          {!!panelType && (
            <InfoItemContainer>
              <InfoItemLabel variant="body2" color="text.secondary">
                Panel Type:
              </InfoItemLabel>
              <InfoItemValue variant="body2">{panelType}</InfoItemValue>
            </InfoItemContainer>
          )}
        </TestNameContainer>
      </Box>
    );
  };

  const getHelperText = () => {
    if (!props.isValid || !props.data) {
      return undefined;
    }
    const selectedOption = JSON.parse(props.data);
    return [
      selectedOption.specimenType
        ? `Specimen Type: ${selectedOption.specimenType}`
        : undefined,
      selectedOption.panelType
        ? `Type: ${selectedOption.panelType}`
        : undefined,
      selectedOption.specimenSource
        ? `Specimen Source: ${selectedOption.specimenSource}`
        : undefined,
    ]
      .filter(i => !!i)
      .join(", ");
  };

  const { initialOptions, initialOption } = getInitialFilterOptions();
  const [prevLabId, setPrevLabId] = useState<string>();

  const helperText = getHelperText();

  useEffect(() => {
    if (!prevLabId) {
      setPrevLabId(props.config?.selectedLab?.id);
      return;
    }

    setPrevLabId(props.config?.selectedLab?.id);
    const { initialOptions } = getInitialFilterOptions();
    clear(initialOptions);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.config?.selectedLab?.id]);

  return (
    <JsonFormsAutocomplete
      data={props.data}
      enabled={props.enabled}
      handleChange={props.handleChange}
      initialOptions={initialOptions}
      label={props.schema.title || props.label || ""}
      path={props.path}
      renderOption={renderOption}
      required={props.required}
      uischema={props.uischema}
      helperText={helperText}
      config={props.config}
      id={props.id}
      initialOption={initialOption}
      ref={autoCompleteRef}
    ></JsonFormsAutocomplete>
  );
};

const Label = styled(Typography)`
  color: ${theme.palette.secondary.main};
`;

const Value = styled(Typography)`
  margin-left: 5px;
`;

const InfoItemContainer = styled(Box)`
  display: flex;
  align-items: baseline;
  margin-right: 10px;
`;

const InfoItemLabel = styled(Label)`
  font-size: 12px;
`;

const InfoItemValue = styled(Value)`
  font-size: 12px;
`;

const TestNameContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  padding-top: 5px;
  gap: 5px;
`;

const TestName = styled(Typography)`
  display: inline;
  font-weight: 500;
  color: ${theme.palette.text.primary};
  line-height: 1.3;
`;

const TestType = styled(Typography)`
  display: inline;
  font-size: 11px;
  margin-left: 5px;
  color: ${theme.palette.secondary.main};
`;

export const labTestAutocompleteCellTester: RankedTester = rankWith(
  10,
  and(formatIs("lab-tests-autocomplete")),
);

export default withJsonFormsOneOfEnumCellProps(
  withTranslateProps(React.memo(LabTestAutocompleteCell)),
  false,
);
